/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo } from 'react';
import qs from 'qs';
import {
  Table,
  Select,
  Input,
  Tooltip,
  Tag,
  message,
} from 'antd';
import { SearchOutlined, CopyOutlined } from '@ant-design/icons';
import debounce from 'lodash.debounce';
import { useHistory, useLocation } from 'react-router-dom';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { getPortfolio } from '@src/services/Admin';
import Loader from '../../components/Loader';
import CancelingSign from '../../components/CancelingSign';
import { APP } from '../../services/Constants';
import divMaxWidth from '../../utils/divMaxWidth';

// Bundle plans are not entries in APP — they are Stripe `metadata.app` values on
// the central workspace subscription. "buddies" is Culture OS (every app except
// SimplePerf), "buddies_perf" adds it.
const BUNDLE = {
  buddies: { name: 'Buddies', emoji: '👯', color: 'purple' },
  buddies_perf: { name: 'Buddies + Perf', emoji: '👯🎯', color: 'magenta' },
};

const planMeta = (key) => APP[key] || BUNDLE[key] || { name: key, emoji: '❔' };

const PLAN_FILTERS = {
  ALL: () => true,
  BUDDIES: (c) => c.bundle === 'buddies',
  BUDDIES_PERF: (c) => c.bundle === 'buddies_perf',
  SINGLE_APP: (c) => c.planType === 'APPS' && c.subscriptions.length === 1,
  MULTI_APP: (c) => c.planType === 'APPS' && c.subscriptions.length > 1,
};

const money = (n) => `$${(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

const signed = (n) => `${n > 0 ? '+' : ''}${money(n).replace('$-', '-$')}`;

const UP = '#52c41a';
const DOWN = '#ff4d4f';

// Shown wherever there is no 90-day-ago figure to compare against.
const NO_VALUE = <span style={{ color: '#bbb' }}>-</span>;

const INTERVAL_LABEL = { month: 'M', year: 'Y', mixed: 'M+Y' };
const INTERVAL_TEXT = {
  month: 'billed monthly',
  year: 'billed yearly',
  mixed: 'mixed monthly + yearly items',
};
const intervalText = (interval) => INTERVAL_TEXT[interval] || `billed ${interval || '?'}`;

const copy = (text) => {
  navigator.clipboard.writeText(text);
  message.success('Copied to clipboard');
};

/**
 * Detail lines shown when hovering a subscription chip.
 */
function SubscriptionTooltip({ subscription }) {
  const s = subscription;
  return (
    <div style={{ lineHeight: 1.6 }}>
      <div><strong>{planMeta(s.app).name}</strong></div>
      <div>{`${money(s.mrr)} / month`}</div>
      <div>{`${s.seats} seats · ${intervalText(s.interval)}`}</div>
      {s.discountPercent > 0 && <div>{`${s.discountPercent}% discount`}</div>}
      {s.currency && s.currency !== 'usd' && (
        <div>{`Currency: ${s.currency.toUpperCase()}`}</div>
      )}
      {s.status && s.status !== 'active' && <div>{`Status: ${s.status}`}</div>}
      {s.cancelAt && (
        <div style={{ color: '#ff7875' }}>
          {`Canceled, active until ${moment(s.cancelAt).format('YYYY-MM-DD')} (not counted in MRR)`}
        </div>
      )}
      <div style={{ opacity: 0.7, marginTop: 4 }}>{s.subscriptionId}</div>
    </div>
  );
}

SubscriptionTooltip.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  subscription: PropTypes.object.isRequired,
};

function Portfolio() {
  const location = useLocation();
  const history = useHistory();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });
  const currentPage = +search.page || 1;
  const pageSize = +search.pageSize || 20;
  const planFilter = search.f || 'ALL';
  const appFilter = search.app || 'ALL';

  const pushSearch = (patch) => history.push({
    ...location,
    search: qs.stringify({ ...search, ...patch }),
  });

  const { isLoading, data } = useQuery(['getPortfolio'], getPortfolio, { cacheTime: 0 });

  const customers = data?.customers || [];
  // Null until the snapshots span the full window; the column is dropped rather
  // than showing a narrower comparison under a 90d heading.
  const growthWindow = data?.growthWindow || null;

  const rows = useMemo(() => {
    const q = (search.q || '').trim().toLowerCase();
    return customers.filter((c) => {
      if (!PLAN_FILTERS[planFilter]?.(c)) return false;
      if (appFilter !== 'ALL'
        && !c.installedApps.includes(appFilter)
        && !c.subscriptions.some((s) => s.app === appFilter)) return false;
      if (!q) return true;
      return [
        c.name, c.domain, c.email, c.customerId,
        ...c.teamIds, ...c.tenantIds,
      ].some((field) => field && String(field).toLowerCase().includes(q));
    });
  }, [customers, search.q, planFilter, appFilter]);

  return (
    <div id="Portfolio">
      {isLoading && <Loader />}
      {data && (
        <>
          <div className="Filters">
            <Select
              value={planFilter}
              style={{ width: 180, marginRight: 8, marginBottom: 6 }}
              onChange={(f) => pushSearch({ f, page: 1 })}
            >
              <Select.Option value="ALL">All plans</Select.Option>
              <Select.Option value="BUDDIES">👯 Buddies</Select.Option>
              <Select.Option value="BUDDIES_PERF">👯🎯 Buddies + Perf</Select.Option>
              <Select.Option value="SINGLE_APP">Single app</Select.Option>
              <Select.Option value="MULTI_APP">Multi-app</Select.Option>
            </Select>
            <Tooltip title="Customers who pay for this app or have it installed">
              <Select
                value={appFilter}
                style={{ width: 180, marginRight: 8, marginBottom: 6 }}
                onChange={(app) => pushSearch({ app, page: 1 })}
              >
                <Select.Option value="ALL">Any app</Select.Option>
                {Object.entries(APP).map(([key, { name, emoji }]) => (
                  <Select.Option value={key} key={key}>{`${emoji} ${name}`}</Select.Option>
                ))}
              </Select>
            </Tooltip>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Name, domain, email, team or customer id"
              defaultValue={search.q || ''}
              style={{ width: 320, marginBottom: 6 }}
              onChange={debounce((e) => pushSearch({ q: e.target.value, page: 1 }), 500)}
              allowClear
            />
          </div>
          <Table
            dataSource={rows}
            loading={isLoading}
            rowKey="customerId"
            size="small"
            scroll={{ x: 1500 }}
            pagination={{
              current: currentPage,
              pageSize,
              total: rows.length,
              showSizeChanger: true,
              pageSizeOptions: ['20', '50', '100', '500'],
              showTotal: (total) => `${total} customer${total === 1 ? '' : 's'}`,
            }}
            onChange={(pagination) => pushSearch({
              page: pagination.current,
              pageSize: pagination.pageSize,
            })}
          >
            <Table.Column
              title="Customer"
              key="customerId"
              width={260}
              render={(_, c) => (
                <Tooltip
                  title={(
                    <div style={{ lineHeight: 1.6 }}>
                      {c.email && <div>{c.email}</div>}
                      {c.teamIds.map((id) => <div key={id}>{`teamId: ${id}`}</div>)}
                      {c.tenantIds.map((id) => <div key={id}>{`tenantId: ${id}`}</div>)}
                      <div>{`stripe: ${c.customerId}`}</div>
                    </div>
                  )}
                >
                  <div>
                    {divMaxWidth(240)(c.name || c.domain || c.customerId)}
                    {c.domain && c.name && (
                      <div style={{ fontSize: 11, color: 'grey' }}>
                        {divMaxWidth(240)(c.domain)}
                      </div>
                    )}
                  </div>
                </Tooltip>
              )}
            />
            <Table.Column
              title="MRR"
              dataIndex="mrr"
              key="customerId"
              align="right"
              width={110}
              defaultSortOrder="descend"
              sorter={(a, b) => a.mrr - b.mrr}
              render={(mrr) => (
                <span style={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{money(mrr)}</span>
              )}
            />
            {growthWindow && (
              <Table.Column
                title={(
                  <Tooltip
                    title={`MRR change ${growthWindow.from} → ${growthWindow.to}, from the daily
                      snapshots. Customers with no figure recorded 90 days ago show "-": those on
                      Seed, Wishly or SimplePerf alone, which are never snapshotted, and those who
                      started paying inside the window.`}
                  >
                    <span style={{ cursor: 'help' }}>{`Δ ${growthWindow.days}d`}</span>
                  </Tooltip>
                )}
                key="customerId"
                align="right"
                width={120}
                // Rows without a baseline sort below every real figure rather
                // than mixing in as if they were flat.
                sorter={(a, b) => (a.growth?.delta ?? -Infinity) - (b.growth?.delta ?? -Infinity)}
                render={(_, c) => {
                  if (!c.growth) return NO_VALUE;
                  return (
                    <Tooltip
                      title={`${money(c.growth.fromMrr)} → ${money(c.growth.toMrr)} over ${c.growth.actualDays}d`}
                    >
                      <span
                        style={{
                          whiteSpace: 'nowrap',
                          cursor: 'help',
                          fontWeight: 600,
                          color: c.growth.delta >= 0 ? UP : DOWN,
                        }}
                      >
                        {signed(c.growth.delta)}
                      </span>
                    </Tooltip>
                  );
                }}
              />
            )}
            {growthWindow && (
              <Table.Column
                title={(
                  <Tooltip title={`Same change relative to MRR on ${growthWindow.from}`}>
                    <span style={{ cursor: 'help' }}>{`Δ ${growthWindow.days}d %`}</span>
                  </Tooltip>
                )}
                key="customerId"
                align="right"
                width={100}
                sorter={(a, b) => (
                  (a.growth?.percent ?? -Infinity) - (b.growth?.percent ?? -Infinity)
                )}
                render={(_, c) => {
                  // percent is null when the baseline itself was 0, which has no
                  // denominator even though the absolute change is real.
                  if (!c.growth || c.growth.percent === null) return NO_VALUE;
                  const up = c.growth.percent >= 0;
                  return (
                    <span
                      style={{ whiteSpace: 'nowrap', fontWeight: 600, color: up ? UP : DOWN }}
                    >
                      {`${up ? '+' : ''}${c.growth.percent}%`}
                    </span>
                  );
                }}
              />
            )}
            <Table.Column
              title="Plan"
              key="customerId"
              width={150}
              render={(_, c) => {
                if (c.bundle) {
                  const meta = BUNDLE[c.bundle];
                  return <Tag color={meta.color}>{`${meta.emoji} ${meta.name}`}</Tag>;
                }
                return (
                  <Tag color={c.subscriptions.length > 1 ? 'blue' : 'default'}>
                    {c.subscriptions.length > 1
                      ? `${c.subscriptions.length} apps`
                      : 'Single app'}
                  </Tag>
                );
              }}
            />
            <Table.Column
              title="Paying for"
              key="customerId"
              width={130}
              render={(_, c) => (
                <div style={{ whiteSpace: 'nowrap' }}>
                  {c.subscriptions.map((s) => (
                    <Tooltip
                      key={s.subscriptionId}
                      title={<SubscriptionTooltip subscription={s} />}
                    >
                      <span style={{ marginRight: 4, cursor: 'help', opacity: s.cancelAt ? 0.4 : 1 }}>
                        {planMeta(s.app).emoji}
                      </span>
                    </Tooltip>
                  ))}
                </div>
              )}
            />
            <Table.Column
              title="Installed"
              key="customerId"
              width={150}
              render={(_, c) => {
                if (!c.installedApps.length) return '—';
                return (
                  <div style={{ whiteSpace: 'nowrap' }}>
                    {c.installedApps.map((app) => (
                      <Tooltip key={app} title={APP[app]?.name || app}>
                        <a
                          href={`#/${app}/workspaces?q=${encodeURIComponent(c.teamIds[0] || '')}`}
                          style={{ marginRight: 4 }}
                        >
                          {APP[app]?.emoji || '❔'}
                        </a>
                      </Tooltip>
                    ))}
                  </div>
                );
              }}
            />
            <Table.Column
              title="Billing"
              key="customerId"
              width={130}
              render={(_, c) => {
                const seats = c.subscriptions.reduce((max, s) => Math.max(max, s.seats), 0);
                const intervals = [...new Set(c.subscriptions.map((s) => s.interval))];
                const discount = c.subscriptions.find((s) => s.discountPercent > 0);
                // `M / Y` means separate monthly and yearly subscriptions;
                // `M+Y` is one subscription whose line items mix intervals.
                const intervalTitle = intervals.map(intervalText).join(', ');
                return (
                  <div style={{ whiteSpace: 'nowrap' }}>
                    <Tooltip title={intervalTitle}>
                      <Tag>{intervals.map((i) => INTERVAL_LABEL[i] || i).join(' / ')}</Tag>
                    </Tooltip>
                    <Tooltip title="Largest billed seat count">
                      <span>{`🪑 ${seats}`}</span>
                    </Tooltip>
                    {discount && (
                      <Tooltip title={`${discount.discountPercent}% discount`}>
                        <span style={{ marginLeft: 6 }}>🏷️</span>
                      </Tooltip>
                    )}
                  </div>
                );
              }}
            />
            <Table.Column
              title="Since"
              key="customerId"
              width={110}
              sorter={(a, b) => {
                const first = (c) => Math.min(...c.subscriptions
                  .map((s) => (s.createdAt ? new Date(s.createdAt).getTime() : Infinity)));
                return first(a) - first(b);
              }}
              render={(_, c) => {
                const dates = c.subscriptions.map((s) => s.createdAt).filter(Boolean).sort();
                if (!dates.length) return '—';
                return (
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {moment(dates[0]).format('YYYY-MM-DD')}
                  </span>
                );
              }}
            />
            <Table.Column
              title="Renewal"
              key="customerId"
              width={130}
              render={(_, c) => {
                const next = c.subscriptions
                  .map((s) => s.currentPeriodEnd)
                  .filter(Boolean)
                  .sort()[0];
                const canceling = c.subscriptions.filter((s) => s.cancelAt);
                if (!next) return '—';
                return (
                  <span style={{ whiteSpace: 'nowrap', color: canceling.length ? 'red' : undefined }}>
                    {moment(next).format('YYYY-MM-DD')}
                    {canceling.length > 0 && (
                      <CancelingSign
                        title={(
                          <div>
                            {canceling.map((s) => (
                              <div key={s.subscriptionId}>
                                {`${planMeta(s.app).name}: canceled, active until ${moment(s.cancelAt).format('YYYY-MM-DD')}`}
                              </div>
                            ))}
                            <div style={{ opacity: 0.7 }}>Not counted in MRR</div>
                          </div>
                        )}
                      />
                    )}
                  </span>
                );
              }}
            />
            <Table.Column
              title=""
              key="customerId"
              width={60}
              render={(_, c) => (
                <div style={{ whiteSpace: 'nowrap' }}>
                  <Tooltip title="Open in Stripe">
                    <a
                      href={`https://dashboard.stripe.com/customers/${c.customerId}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{ marginRight: 8 }}
                    >
                      💳
                    </a>
                  </Tooltip>
                  {c.teamIds[0] && (
                    <Tooltip title={`Copy ${c.teamIds[0]}`}>
                      <CopyOutlined
                        style={{ cursor: 'pointer' }}
                        onClick={() => copy(c.teamIds[0])}
                      />
                    </Tooltip>
                  )}
                </div>
              )}
            />
          </Table>
        </>
      )}
    </div>
  );
}

export default Portfolio;
